import { Anchor, Text } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { townCrierUrl } from './scheduleFormat'

/**
 * The hand-off to Cartographer's Town Crier — the only "act" this page has (S1).
 *
 * A real link only when this build knows where Cartographer is. Otherwise the
 * destination is NAMED, not linked: the served path is relative to Cartographer,
 * and as a bare `href` it would resolve against Observatory's own origin, which
 * has no such route. A dead link dressed as a hand-off is a fake affordance.
 *
 * `compact` is the per-occurrence form: the same destination on every row,
 * because Town Crier takes no schedule id. The row's label says which schedule
 * to look for; the link cannot open it directly, and the page says so once,
 * beside the page-level link, rather than on every row.
 */
export function TownCrierLink({
  path, scheduleName, compact = false,
}: {
  path: string
  scheduleName?: string
  compact?: boolean
}) {
  const url = townCrierUrl(path)

  if (!url) {
    if (compact) return null
    return (
      <Text size="sm" c="dimmed">
        Town Crier, at <code>{path}</code> in Cartographer — not linked, because this build of Observatory
        has no Cartographer address configured (<code>VITE_CARTOGRAPHER_URL</code>).
      </Text>
    )
  }

  return (
    <Anchor
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      size="sm"
      aria-label={
        scheduleName
          ? `Open Town Crier in Cartographer to find the schedule "${scheduleName}"`
          : 'Open Town Crier in Cartographer'
      }
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
        {compact ? 'Town Crier' : 'Open Town Crier in Cartographer'}
        <IconExternalLink size={13} />
      </span>
    </Anchor>
  )
}
